import { useEffect, useMemo, useState } from 'react';
import { type ChannelMember, type Task } from '../lib/api';
import { useTaskStore } from '../stores/useTaskStore';
import { STATUS_CONFIG } from '../lib/taskStatus';
import { UserAvatar } from './UserAvatar';
import styles from './MemberProjectProfilePopover.module.css';

type LoadState = { kind: 'loading' } | { kind: 'ready' } | { kind: 'error' };

function relationFor(task: Task, userId: string): string {
  if (task.assignee_id === userId) return 'Assigned';
  if (task.created_by === userId) return 'Created';
  return 'Participant';
}

function TaskRow({ task, userId }: { task: Task; userId: string }) {
  const status = STATUS_CONFIG[task.status];
  return (
    <li className={styles.task}>
      <span
        className={styles.status}
        style={{ background: status?.color ?? '#a1a1aa' }}
        aria-hidden="true"
      />
      <span className={styles.taskCopy}>
        <strong title={task.title}>{task.title}</strong>
        <small>
          {status?.label ?? task.status} · {relationFor(task, userId)}
        </small>
      </span>
    </li>
  );
}

/** A compact Project-member profile based solely on the authorized task read model. */
export default function MemberProjectProfilePopover({
  guildId,
  member,
}: {
  guildId: string;
  member: ChannelMember;
}) {
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' });
  const taskById = useTaskStore((state) => state.byTaskId);

  useEffect(() => {
    let stale = false;
    setLoadState({ kind: 'loading' });
    useTaskStore.getState().loadGuildTasks(guildId)
      .then(() => { if (!stale) setLoadState({ kind: 'ready' }); })
      .catch(() => { if (!stale) setLoadState({ kind: 'error' }); });
    return () => { stale = true; };
  }, [guildId]);

  const work = useMemo(() => {
    if (loadState.kind !== 'ready') return null;
    const related = (Object.values(taskById) as Task[])
      .filter((task) => task.guild_id === guildId)
      .filter(
        (task) =>
          task.created_by === member.user_id ||
          task.assignee_id === member.user_id ||
          task.participants?.includes(member.user_id),
      )
      .sort((a, b) => b.updated_at - a.updated_at);
    const current = related.filter(
      (task) =>
        task.status === 'in_progress' || task.status === 'in_review' || task.status === 'open',
    );
    const completed = related.filter((task) => task.status === 'done');
    return {
      active: related.filter((task) => task.status === 'in_progress' || task.status === 'in_review')
        .length,
      open: related.filter((task) => task.status === 'open').length,
      completed: completed.length,
      current: current.slice(0, 2),
      recent: completed.slice(0, 1),
    };
  }, [guildId, loadState, member.user_id, taskById]);

  const joined = member.joined_at ? new Date(member.joined_at).toLocaleDateString() : null;
  return (
    <section className={styles.root} aria-label={`${member.display_name} Project profile`}>
      <header className={styles.identity}>
        <UserAvatar
          userId={member.user_id}
          displayName={member.display_name}
          role={member.role}
          avatarUrl={member.avatar_url}
          state={member.state}
          size={32}
        />
        <div className={styles.identityMeta}>
          <strong>{member.display_name}</strong>
          {member.role === 'agent' && <span className={styles.agentLabel}>APP</span>}
          {joined && <span>Joined {joined}</span>}
        </div>
      </header>
      <div className={styles.content}>
        <section aria-labelledby="contribution-summary">
          <h2 id="contribution-summary">In this Project</h2>
          {loadState.kind === 'loading' && (
            <p className={styles.muted} role="status">
              Loading authorized task context…
            </p>
          )}
          {loadState.kind === 'error' && (
            <p className={styles.muted} role="alert">
              Task context is unavailable right now.
            </p>
          )}
          {work && (
            <div className={styles.summary} aria-label="Task contribution summary">
              <span>
                <strong>{work.active}</strong> active
              </span>
              <span>
                <strong>{work.open}</strong> open
              </span>
              <span>
                <strong>{work.completed}</strong> completed
              </span>
            </div>
          )}
        </section>
        {work && (
          <section aria-labelledby="current-focus">
            <h2 id="current-focus">Current focus</h2>
            {work.current.length ? (
              <ul className={styles.list}>
                {work.current.map((task) => (
                  <TaskRow key={task.id} task={task} userId={member.user_id} />
                ))}
              </ul>
            ) : (
              <p className={styles.muted}>No open work in the authorized task view.</p>
            )}
          </section>
        )}
        {work && work.recent.length > 0 && (
          <section aria-labelledby="recent-work">
            <h2 id="recent-work">Recent work</h2>
            <ul className={styles.list}>
              {work.recent.map((task) => (
                <TaskRow key={task.id} task={task} userId={member.user_id} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </section>
  );
}
